// src/utils/invoiceClosureSync.js
// ============================================================================
// INVOICING / CLOSURE ROW DATE SYNC — single source of truth
// ============================================================================
// The planning timeline's "Invoicing of <stage>" and "Closure of <stage>" system rows derive their
// dates from the parent stage's billing fields (invoice date, paid date, actual completion date).
// This helper computes those derived dates so every consumer stays consistent:
//
//   • the billing page (TimelinePage) — writes them when the user edits Invoice/Paid,
//   • the planning page (TimelinesPage) — backfills them on load and shows the sync status disc,
//   • the planning page's start-row cascade — reads the payment-terms WINDOW + the delay lag.
//
// Rules (per product spec):
//   Anchor         = the client APPROVAL date (billing page "Appr."), falling back to the invoice date for
//                    stages recorded before approval dates were kept.
//   Invoicing row  start = anchor
//                  end   = PAID date once paid; otherwise anchor + duration in CALENDAR days (the window end).
//   Closure row    date  = paid on time    → stage's actual completion date (fallback: paid date)
//                          paid late       → the paid date
//                          unpaid + overdue → today (advances daily until payment is recorded)
//                          otherwise       → none
//   Delay lag      = the number of days the delay endpoint (paid date when paid late, today while unpaid
//                    + overdue) runs past the window end. NOTE: the AUTHORITATIVE lag used for scheduling
//                    (the start row's FS link to the next stage) is counted in TimelinesPage as WORKING days
//                    INCLUDING brio holidays. The `delayLag` returned here is a plain calendar delta.
//   "Paid late"    = paid date is after the window end.
//
// Dates are ISO 'YYYY-MM-DD' strings, so lexicographic comparison is chronological.

// Add whole CALENDAR days to a 'YYYY-MM-DD' string; returns a 'YYYY-MM-DD' string (or null).
export function addCalendarDaysStr(dateStr, days) {
  if (!dateStr || days == null) return null;
  const d = new Date(dateStr + 'T00:00:00');
  if (isNaN(d.getTime())) return null;
  d.setDate(d.getDate() + Number(days));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Whole CALENDAR days between two 'YYYY-MM-DD' strings (toStr - fromStr). Positive when toStr is later;
// 0 when either is missing/invalid. Counts every day — weekends and holidays included.
export function calendarDaysBetween(fromStr, toStr) {
  if (!fromStr || !toStr) return 0;
  const a = new Date(fromStr + 'T00:00:00');
  const b = new Date(toStr + 'T00:00:00');
  if (isNaN(a.getTime()) || isNaN(b.getTime())) return 0;
  return Math.round((b.getTime() - a.getTime()) / 86400000);
}

/**
 * Compute the derived Invoicing/Closure row dates for one stage (or part-stage).
 *
 * @param {Object} p
 * @param {string|null} [p.approvalDate] - stage's client approval date (YYYY-MM-DD); anchors the invoicing row
 * @param {string|null} p.invoiceDate   - stage's invoice-sent date (YYYY-MM-DD); anchor fallback
 * @param {string|null} p.paidDate      - stage's payment-received date (YYYY-MM-DD)
 * @param {string|null} p.actualDate    - stage's actual completion date (YYYY-MM-DD)
 * @param {number|null} p.durationDays  - invoicing row's duration / payment terms (calendar days)
 * @param {string|null} [p.todayStr]    - today's date (YYYY-MM-DD); enables the unpaid-overdue branch so
 *                                        the closure date advances to today while payment is late.
 * @returns {{
 *   anchorDate: string|null,     // approval date, else invoice date
 *   windowEnd: string|null,      // anchor + duration: the payment window end (delay reference)
 *   paidLate: boolean,           // paid after the window
 *   overdue: boolean,            // unpaid AND today is past the window (needs todayStr)
 *   delayLag: number,            // CALENDAR days the delay endpoint runs past the window end (0 if none)
 *   invoicingStart: string|null, // = anchor
 *   invoicingEnd: string|null,   // paid → paid; else window end
 *   closureDate: string|null,    // on time → actual (fallback paid); late → paid; unpaid+overdue → today; else null
 * }}
 */
export function computeInvoicingClosureDates({ approvalDate, invoiceDate, paidDate, actualDate, durationDays, todayStr }) {
  const anchor = approvalDate || invoiceDate || null;
  const paid = paidDate || null;
  const actual = actualDate || null;
  const today = todayStr || null;
  const dur = durationDays != null && durationDays !== '' ? Number(durationDays) : null;

  const windowEnd = (anchor && dur != null) ? addCalendarDaysStr(anchor, dur) : null;
  // Only decide "late" / "overdue" when we actually know the window; without it we can't prove lateness.
  const paidLate = !!(paid && windowEnd && paid > windowEnd);
  const overdue = !paid && !!(windowEnd && today && today > windowEnd);

  const invoicingStart = anchor;
  // Invoicing END: the paid date once paid; until then the window end (anchor + duration).
  const invoicingEnd = paid ? paid : windowEnd;

  let closureDate = null;
  if (paid) closureDate = paidLate ? paid : (actual || paid);
  else if (overdue) closureDate = today;

  // Delay endpoint = how far past the window the payment (or the running clock) has reached.
  const delayEndpoint = paidLate ? paid : (overdue ? today : null);
  const delayLag = delayEndpoint ? Math.max(0, calendarDaysBetween(windowEnd, delayEndpoint)) : 0;

  return { anchorDate: anchor, windowEnd, paidLate, overdue, delayLag, invoicingStart, invoicingEnd, closureDate };
}

// Today as a local 'YYYY-MM-DD' string, for the overdue branch above.
export function localTodayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Derived dates for one stage / part record (its billing fields) — the one call every page makes, so the
// field mapping (Appr. → clientApprovalDate, Actual → actualDate …) lives in one place.
export function deriveRowDates(rec, durationDays, todayStr = localTodayStr()) {
  const r = rec || {};
  return computeInvoicingClosureDates({
    approvalDate: r.clientApprovalDate || null,
    invoiceDate: r.invoiceDate || null,
    paidDate: r.paidDate || null,
    actualDate: r.actualDate || null,
    durationDays,
    todayStr,
  });
}

// ── Paid date: the Invoices page wins ───────────────────────────────────────
// A stage's paid date lives in two places: `paidDate` on the stage (or part) in the stage timeline,
// and `paid_date` on its invoice. When the invoice is confirmed paid, the invoice's date is the truth
// and the billing page already shows it. The timeline copy can be stale (typed in when the invoice
// went out, before payment), so every consumer that derives the Invoicing / Closure rows resolves the
// paid date through here instead of reading `paidDate` straight off the timeline.
//
// `invoiceSummary` is the shape returned by fetchInvoiceSummaryForProject: keyed by stage key, and by
// `${stageKey}__part__${partId}` for part stages.

// The confirmed paid date for one summary entry, or null when no invoice is confirmed paid.
export function invoicePaidDate(summaryEntry) {
  return summaryEntry?.hasPaidConfirmed ? (summaryEntry.paidDate || null) : null;
}

// Return `timeline` with every stage's and part's `paidDate` replaced by its confirmed invoice paid
// date wherever the two differ. Returns the SAME object when nothing differs, so it is safe to memo on
// and cheap to compare. Only stage keys present in the summary are touched.
export function applyInvoicePaidDates(timeline, invoiceSummary) {
  if (!timeline || !invoiceSummary) return timeline;
  let out = timeline;
  for (const [key, stageTl] of Object.entries(timeline)) {
    if (!stageTl || typeof stageTl !== 'object' || key.startsWith('_')) continue;
    let nextStage = stageTl;
    const stagePaid = invoicePaidDate(invoiceSummary[key]);
    if (stagePaid && stageTl.paidDate !== stagePaid) nextStage = { ...nextStage, paidDate: stagePaid };
    if (Array.isArray(stageTl.partStages) && stageTl.partStages.length) {
      let partsChanged = false;
      const nextParts = stageTl.partStages.map((p) => {
        const partPaid = invoicePaidDate(invoiceSummary[`${key}__part__${p.id}`]);
        if (partPaid && p.paidDate !== partPaid) { partsChanged = true; return { ...p, paidDate: partPaid }; }
        return p;
      });
      if (partsChanged) nextStage = { ...nextStage, partStages: nextParts };
    }
    if (nextStage !== stageTl) {
      if (out === timeline) out = { ...timeline };
      out[key] = nextStage;
    }
  }
  return out;
}
